import React, { useEffect, useState } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import { getSmallAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/functions.ts'
import { EcommerceResponse, IEcommerceQuestionSchema } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import style from './Cart.module.scss'
import CartCounter from './CartCounter.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'

interface ICartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  totals?: number[]
  findability: boolean
  renderError: () => React.ReactNode
  onAddToCart: (qnt: 1 | -1, index: number) => void
  onAddToCartFromCounter: (qnt: number, index: number) => void
  onItemSelect: (index: number) => void
  onUpdateResponse?: (response: EcommerceResponse) => void
  checkout?: () => void
  scrollTop: () => void
}

const Cart = ({
  response,
  schema,
  totals,
  findability,
  renderError,
  onAddToCart,
  onAddToCartFromCounter,
  onItemSelect,
  onUpdateResponse,
  checkout,
  scrollTop
}: ICartProps) => {
  const [showCounter, setShowCounter] = useState<{ show: boolean; index: number }>({ show: false, index: null })
  const viewport = useViewport()
  const timeline = useTimeline()
  const { t, languageTag } = useSurveyLocale()
  useEffect(() => {
    scrollTop()
  }, [scrollTop])
  const na = schema.answers.find(answer => answer.na)
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)

  const onNaSelect = () => {
    timeline.log({
      type: 'na_select',
      ref: na.id
    })
    const answers = response.answers.map(answer => {
      answer.v = answer.id === na.id ? 1 : 0
      return answer
    })
    response.answers = answers
    onUpdateResponse(response)
  }

  return (
    <section className={`${style.cart} ${viewport.mobile ? style.cartMobile : ''}`}>
      {showCounter.show && (
        <CartCounter
          quantity={response.answers[showCounter.index]?.v || 0}
          index={showCounter.index}
          schema={schema}
          onCloseCounter={() => setShowCounter({ show: false, index: null })}
          onAddToCartFromCounter={onAddToCartFromCounter}
        />
      )}
      <div className={style.products}>
        <h6>{t('questions', 'ec', 'nike', 'bag')}</h6>
        {totals[0] > 0 ? (
          <>
            {response.answers.map(
              (answer, index) =>
                answer.v > 0 && (
                  <div className={style.product} key={index}>
                    <div className={style.content}>
                      {schema.answers[index].image ? (
                        <div
                          onClick={() => onItemSelect(index)}
                          className={`
                            ${style.image}
                            ${
                              !schema.answers[index].not_apply_filter &&
                              schema.answers[index].image.width != schema.answers[index].image.height
                                ? style.imageFilter
                                : ''
                            }
                          `}
                          role="img"
                          aria-label={schema.answers[index].image?.alt_text || t('questions', 'img', 'noAlt')}
                          style={{
                            backgroundImage: `url(${getSmallAssetPath(schema.answers[index].image)})`
                          }}
                        />
                      ) : (
                        <div className={style.noImage} onClick={() => onItemSelect(index)} />
                      )}
                      <div className={style.info}>
                        <div
                          className={style.caption}
                          dangerouslySetInnerHTML={{ __html: schema.answers[index].text }}
                          onClick={() => onItemSelect(index)}
                        />
                        <div className={style.prices}>
                          {schema.answers[index].discounted_price &&
                          schema.answers[index].price > schema.answers[index].discounted_price ? (
                            <>
                              <div
                                className={style.newPrice}
                                dangerouslySetInnerHTML={{
                                  __html: `${formatPriceWithLocale(
                                    schema.answers[index].discounted_price * answer.v,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )}`
                                }}
                              />
                              <div
                                className={style.lastPrice}
                                dangerouslySetInnerHTML={{
                                  __html: `${formatPriceWithLocale(
                                    schema.answers[index].price * answer.v,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )}`
                                }}
                              />
                            </>
                          ) : (
                            <div
                              className={style.defaultPrice}
                              dangerouslySetInnerHTML={{
                                __html: `${formatPriceWithLocale(
                                  schema.answers[index].price * answer.v,
                                  schema.price_mask,
                                  languageTag,
                                  true,
                                  false
                                )}`
                              }}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {viewport.mobile ? (
                      <div className={style.qty} onClick={() => setShowCounter({ show: true, index: index })}>
                        {t('questions', 'ec', 'target', 'qty')}&nbsp;
                        {answer.v}
                        <div className={style.downIcon} />
                      </div>
                    ) : (
                      <div className={style.buttons}>
                        <AddToCartButton
                          quantity={answer.v}
                          findability={findability}
                          index={index}
                          onAddToCart={onAddToCart}
                          limit={schema.max_qty_per_product}
                        />
                        <EmptyLink color="#111">
                          <div className={style.fav}>
                            <div className={style.heartIcon} />
                          </div>
                        </EmptyLink>
                      </div>
                    )}
                    <div className={style.additionalInfo}>
                      <div className={style.title}>{t('questions', 'ec', 'nike', 'shipping')}</div>
                      <div className={style.arrives}>
                        <span
                          className={style.shippingDate}
                          dangerouslySetInnerHTML={{
                            __html: t<(date: string) => string>(
                              'questions',
                              'ec',
                              'nike',
                              'arrivesBy'
                            )(
                              new Date(new Date().setDate(new Date().getDate() + 6)).toLocaleDateString(languageTag, {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })
                            )
                          }}
                        />
                        <EmptyLink color="#111">
                          <span className={style.textUnderlined}>{t('questions', 'ec', 'nike', 'editLocation')}</span>
                        </EmptyLink>
                      </div>
                      <div className={style.pickup}>
                        <div className={style.title}>{t('questions', 'ec', 'nike', 'freePickup')}</div>
                        <EmptyLink color="#111">
                          <span className={style.textUnderlined}>{t('questions', 'ec', 'nike', 'findStore')}</span>
                        </EmptyLink>
                      </div>
                    </div>
                    {viewport.mobile &&
                    schema.answers[index].discounted_price &&
                    schema.answers[index].price > schema.answers[index].discounted_price ? (
                      <div className={style.promo}>
                        <div className={style.promoIcon} />
                        <span
                          className={style.offText}
                          dangerouslySetInnerHTML={{
                            __html: t<(off: string, applied: boolean) => string>(
                              'questions',
                              'ec',
                              'nike',
                              'off'
                            )(
                              (
                                ((schema.answers[index].price - schema.answers[index].discounted_price) /
                                  schema.answers[index].price) *
                                100
                              ).toFixed(1),
                              false
                            )
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                )
            )}
          </>
        ) : (
          <div className={style.emptyCart}>
            {viewport.mobile ? (
              <>
                <div className={style.bag} />
                <div
                  className={style.emptyText}
                  dangerouslySetInnerHTML={{
                    __html: t('questions', 'ec', 'nike', 'emptyCartMobile')
                  }}
                />
                {na && (
                  <button className={style.na} onClick={onNaSelect}>
                    {na.text || t('questions', 'ec', 'na')}
                  </button>
                )}
              </>
            ) : (
              <>
                <p dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'nike', 'emptyCart') }} />
                {na && (
                  <button className={style.na} onClick={onNaSelect}>
                    {na.text || t('questions', 'ec', 'na')}
                  </button>
                )}
              </>
            )}
          </div>
        )}
        <div className={style.bigTitle}>{t('questions', 'ec', 'nike', 'favTitle')}</div>
        <div className={style.favoritesText}>
          <div className={style.favText}>{t('questions', 'ec', 'ecommerce', 'favoritesText')}&nbsp;</div>
          <EmptyLink color="#111">
            <span className={style.favLink}>{t('questions', 'ec', 'ecommerce', 'joinUs')}</span>
          </EmptyLink>
          <span className={style.favText}>&nbsp;{t('questions', 'ec', 'nike', 'or')}&nbsp;</span>
          <EmptyLink color="#111">
            <span className={style.favLink}>{t('questions', 'ec', 'ecommerce', 'signIn')}</span>
          </EmptyLink>
        </div>
      </div>
      {(!viewport.mobile || (viewport.mobile && totals[0] > 0)) && (
        <div className={style.checkout}>
          <h6>{t('questions', 'ec', 'nike', 'summary')}</h6>
          <div className={style.summary}>
            <EmptyLink color="#111">
              <div className={style.row}>
                <span className={style.textMedium}>{t('questions', 'ec', 'nike', 'havePromo')}</span>
                <div className={style.downIcon} />
              </div>
            </EmptyLink>
            <div className={style.row}>
              <div className={style.textLight}>
                <span>{t('questions', 'ec', 'nike', 'subtotal')}</span>
                <EmptyLink color="#111">
                  <div className={style.signIcon} />
                </EmptyLink>
              </div>
              <div
                className={style.textLight}
                dangerouslySetInnerHTML={{
                  __html: `${totals[1] > 0 ? formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false) : '—'}`
                }}
              />
            </div>
            <div className={style.row}>
              <div className={style.textLight}>{t('questions', 'ec', 'nike', 'handling')}</div>
              <div className={style.textLight}>{t('questions', 'ec', 'nike', 'free')}</div>
            </div>
            <div className={style.row}>
              <div className={style.textLight}>
                <span>{t('questions', 'ec', 'nike', 'tax')}</span>
                <EmptyLink color="#111">
                  <div className={style.signIcon} />
                </EmptyLink>
              </div>
              <div className={style.textLight}>—</div>
            </div>
            <div className={style.line} />
            <div className={style.row}>
              <div className={style.textMedium}>
                <span>{t('questions', 'ec', 'nike', 'total')}</span>
              </div>
              <div
                className={style.textMedium}
                dangerouslySetInnerHTML={{
                  __html: `${totals[0] > 0 ? formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false) : '—'}`
                }}
              />
            </div>
            {!viewport.mobile && <div className={style.line} />}
            <div className={style.checkoutTextWrapper}>
              <div
                className={style.checkoutText}
                dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'nike', 'checkoutText') }}
              />
              <EmptyLink color="#111">
                <span className={style.checkoutLink}>{t('questions', 'ec', 'nike', 'joinUs')}</span>
              </EmptyLink>
              <span className={style.checkoutText}>&nbsp;{t('questions', 'ec', 'nike', 'or')}&nbsp;</span>
              <EmptyLink color="#111">
                <span className={style.checkoutLink}>{t('questions', 'ec', 'nike', 'signIn')}</span>
              </EmptyLink>
            </div>
            {viewport.mobile ? '' : renderError()}
            <button
              className={style.checkoutButton}
              onClick={totals[0] > 0 ? checkout : null}
              disabled={0 === totals[0] || !valid}
            >
              {t('questions', 'ec', 'nike', 'checkout')}
            </button>
            <div
              className={style.disclaimer}
              dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'nike', 'disclaimer') }}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default Cart
